/**
 * SubscriptionGate - Shared paywall component for all Quantrolix apps.
 *
 * Copy this file into your app's src/components/ directory.
 * Requires: React 18+, fetch API
 *
 * Usage:
 *   import SubscriptionGate from './components/SubscriptionGate';
 *   function App() {
 *     return (
 *       <SubscriptionGate appId="fire-pos" authUrl="https://apps.quantrolix.ai/api/auth">
 *         <YourAppContent />
 *       </SubscriptionGate>
 *     );
 *   }
 */

import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { isNativeApp } from '../config';

const AUTH_URL = 'https://apps.quantrolix.ai/api/auth';

const SubscriptionContext = createContext({
  user: null,
  subscription: null,
  isLoading: true,
  isAuthenticated: false,
  isSubscribed: false,
  isTrial: false,
  trialDaysLeft: 0,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  subscribe: async () => {},
});

export function useSubscription() {
  return useContext(SubscriptionContext);
}

// ─── Storage Helpers ────────────────────────────────────────────────────────

function getStoredToken(appId) {
  try {
    return localStorage.getItem(`${appId}_token`);
  } catch { return null; }
}

function setStoredToken(appId, token) {
  try {
    localStorage.setItem(`${appId}_token`, token);
  } catch {}
}

function clearStoredToken(appId) {
  try {
    localStorage.removeItem(`${appId}_token`);
    localStorage.removeItem(`${appId}_trial_start`);
  } catch {}
}

function getTrialStart(appId) {
  try {
    return localStorage.getItem(`${appId}_trial_start`);
  } catch { return null; }
}

function setTrialStart(appId) {
  try {
    if (!localStorage.getItem(`${appId}_trial_start`)) {
      localStorage.setItem(`${appId}_trial_start`, new Date().toISOString());
    }
  } catch {}
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function SubscriptionGate({ appId, pricing, authUrl = AUTH_URL, children }) {
  const baseUrl = authUrl || AUTH_URL;
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [screen, setScreen] = useState('loading'); // loading | login | paywall | app
  const [error, setError] = useState('');

  const trialDays = pricing?.trialDays || 7;

  // Calculate trial status
  const trialStart = getTrialStart(appId);
  const trialDaysLeft = trialStart
    ? Math.max(0, trialDays - Math.floor((Date.now() - new Date(trialStart).getTime()) / 86400000))
    : trialDays;
  const isTrial = trialDaysLeft > 0 && !subscription?.active;

  // ─── Auth Functions ─────────────────────────────────────────────────

  const checkAuth = useCallback(async () => {
    if (isNativeApp) {
      setUser({
        id: `${appId}-app-store-review`,
        email: 'review@quantrolix.app',
        name: 'App Store Reviewer',
        tier: 'app-store',
      });
      setSubscription({ active: true, tier: 'app-store' });
      setScreen('app');
      setIsLoading(false);
      return;
    }

    const token = getStoredToken(appId);
    if (!token) {
      setIsLoading(false);
      // No account, but an already-started trial (or an Apple build, per 3.1.1)
      // still gets the app — otherwise every relaunch mid-trial demands sign-in.
      if (isNativeApp || (trialStart && isTrial)) {
        setScreen('app');
      } else {
        setScreen('login');
      }
      return;
    }

    try {
      const res = await fetch(`${baseUrl}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        clearStoredToken(appId);
        setScreen('login');
        setIsLoading(false);
        return;
      }

      const data = await res.json();
      setUser(data.user || data);

      // Check subscription status
      if (data.user?.tier && data.user.tier !== 'free') {
        setSubscription({ active: true, tier: data.user.tier });
        setScreen('app');
      } else if (isNativeApp) {
        // Apple build: fully functional & free, never gate behind a paywall (3.1.1)
        setScreen('app');
      } else if (isTrial) {
        setTrialStart(appId);
        setScreen('app');
      } else {
        setScreen('paywall');
      }
    } catch {
      // Network error - allow trial/offline use
      if (isTrial) {
        setTrialStart(appId);
        setScreen('app');
      } else {
        setScreen('login');
      }
    } finally {
      setIsLoading(false);
    }
  }, [appId, baseUrl, isTrial]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed');
        return false;
      }

      setStoredToken(appId, data.token || data.accessToken);
      setUser(data.user);

      if (data.user?.tier && data.user.tier !== 'free') {
        setSubscription({ active: true, tier: data.user.tier });
        setScreen('app');
      } else if (isNativeApp) {
        // Apple build: fully functional & free, never gate behind a paywall (3.1.1)
        setScreen('app');
      } else {
        setTrialStart(appId);
        if (isTrial || trialDaysLeft > 0) {
          setScreen('app');
        } else {
          setScreen('paywall');
        }
      }
      return true;
    } catch {
      setError('Network error. Please try again.');
      return false;
    }
  };

  const register = async (email, password, name) => {
    setError('');
    try {
      const res = await fetch(`${baseUrl}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return false;
      }

      setStoredToken(appId, data.token || data.accessToken);
      setUser(data.user);
      setTrialStart(appId);
      setScreen('app');
      return true;
    } catch {
      setError('Network error. Please try again.');
      return false;
    }
  };

  const logout = () => {
    clearStoredToken(appId);
    setUser(null);
    setSubscription(null);
    setScreen('login');
  };

  const subscribe = async (tierId) => {
    // Apple builds must not sell digital subscriptions outside Apple IAP (3.1.1).
    if (isNativeApp) return;
    const token = getStoredToken(appId);
    try {
      const res = await fetch(`${baseUrl}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ appId, tierId }),
      });
      const data = await res.json();

      if (data.url) {
        // Open Stripe checkout in default browser (works in Electron)
        if (window.electron?.openExternal) {
          window.electron.openExternal(data.url);
        } else {
          window.open(data.url, '_blank');
        }
      }
    } catch {
      setError('Could not start checkout. Please try again.');
    }
  };

  const contextValue = {
    user,
    subscription,
    isLoading,
    isAuthenticated: !!user,
    isSubscribed: subscription?.active || isNativeApp,
    isTrial: isNativeApp ? false : isTrial,
    trialDaysLeft,
    login,
    register,
    logout,
    subscribe,
  };

  // ─── Render ─────────────────────────────────────────────────────────

  if (screen === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (screen === 'login') {
    return (
      <SubscriptionContext.Provider value={contextValue}>
        <LoginScreen
          appName={pricing?.name || appId}
          error={error}
          onLogin={login}
          onRegister={register}
          onSkipTrial={() => {
            setTrialStart(appId);
            setScreen('app');
          }}
          trialDays={trialDays}
        />
      </SubscriptionContext.Provider>
    );
  }

  if (screen === 'paywall') {
    return (
      <SubscriptionContext.Provider value={contextValue}>
        <PaywallScreen
          appName={pricing?.name || appId}
          tiers={pricing?.tiers || []}
          onSubscribe={subscribe}
          onLogout={logout}
        />
      </SubscriptionContext.Provider>
    );
  }

  // screen === 'app'
  return (
    <SubscriptionContext.Provider value={contextValue}>
      {isTrial && !isNativeApp && (
        <div style={styles.trialBanner}>
          Trial: {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
          <button
            onClick={() => setScreen('paywall')}
            style={styles.trialUpgradeBtn}
          >
            Upgrade
          </button>
        </div>
      )}
      {children}
    </SubscriptionContext.Provider>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

function LoginScreen({ appName, error, onLogin, onRegister, onSkipTrial, trialDays }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (mode === 'login') {
      await onLogin(email, password);
    } else {
      await onRegister(email, password, name);
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>{appName}</h1>
        <p style={styles.subtitle}>
          {mode === 'login' ? 'Sign in to continue' : 'Create your account'}
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {mode === 'register' && (
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={styles.input}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={styles.input}
            required
            minLength={6}
          />

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.primaryBtn} disabled={loading}>
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <button
          onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
          style={styles.linkBtn}
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>

        <div style={styles.divider}>
          <span style={styles.dividerText}>or</span>
        </div>

        <button onClick={onSkipTrial} style={styles.secondaryBtn}>
          Start {trialDays}-Day Free Trial
        </button>
      </div>
    </div>
  );
}

function PaywallScreen({ appName, tiers, onSubscribe, onLogout }) {
  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, maxWidth: '600px' }}>
        <h1 style={styles.title}>Upgrade {appName}</h1>
        <p style={styles.subtitle}>Your trial has ended. Choose a plan to continue.</p>

        <div style={styles.tiersContainer}>
          {tiers.map((tier) => (
            <div
              key={tier.id}
              style={{
                ...styles.tierCard,
                ...(tier.recommended ? styles.tierCardRecommended : {}),
              }}
            >
              {tier.recommended && (
                <span style={styles.recommendedBadge}>Recommended</span>
              )}
              <h3 style={styles.tierName}>{tier.label}</h3>
              <div style={styles.tierPrice}>
                <span style={styles.priceAmount}>
                  ${tier.price.toFixed(2)}
                </span>
                <span style={styles.priceInterval}>/{tier.interval}</span>
              </div>
              <ul style={styles.featureList}>
                {tier.features.map((f, i) => (
                  <li key={i} style={styles.featureItem}>{f}</li>
                ))}
              </ul>
              <button
                onClick={() => onSubscribe(tier.id)}
                style={tier.recommended ? styles.primaryBtn : styles.secondaryBtn}
              >
                Subscribe
              </button>
            </div>
          ))}
        </div>

        <button onClick={onLogout} style={styles.linkBtn}>
          Sign out
        </button>
      </div>
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0f172a',
    color: '#e2e8f0',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '1rem',
  },
  card: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '2rem',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 0 0.5rem',
    color: '#f1f5f9',
  },
  subtitle: {
    textAlign: 'center',
    color: '#94a3b8',
    margin: '0 0 1.5rem',
    fontSize: '0.95rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  input: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#e2e8f0',
    fontSize: '1rem',
    outline: 'none',
  },
  primaryBtn: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    width: '100%',
  },
  secondaryBtn: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: '1px solid #475569',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: '1rem',
    cursor: 'pointer',
    width: '100%',
  },
  linkBtn: {
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    fontSize: '0.9rem',
    cursor: 'pointer',
    padding: '0.75rem',
    textAlign: 'center',
    width: '100%',
  },
  error: {
    color: '#f87171',
    fontSize: '0.9rem',
    margin: 0,
    textAlign: 'center',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem 0',
    gap: '0.75rem',
  },
  dividerText: {
    color: '#64748b',
    fontSize: '0.85rem',
    flex: 'none',
  },
  trialBanner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
    color: '#fff',
    textAlign: 'center',
    padding: '8px 16px',
    fontSize: '0.85rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
  },
  trialUpgradeBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#fff',
    padding: '3px 12px',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontWeight: 600,
  },
  tiersContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  tierCard: {
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '1.25rem',
    position: 'relative',
  },
  tierCardRecommended: {
    border: '2px solid #3b82f6',
    background: 'rgba(59,130,246,0.05)',
  },
  recommendedBadge: {
    position: 'absolute',
    top: '-10px',
    right: '12px',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '2px 10px',
    borderRadius: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tierName: {
    margin: '0 0 0.5rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#f1f5f9',
  },
  tierPrice: {
    marginBottom: '0.75rem',
  },
  priceAmount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#f1f5f9',
  },
  priceInterval: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 1rem',
  },
  featureItem: {
    padding: '4px 0',
    fontSize: '0.9rem',
    color: '#94a3b8',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #334155',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#94a3b8',
    marginTop: '1rem',
  },
};
